"use client";

import { CSSProperties, FC, HTMLAttributes } from "react";
import Lottie from "react-lottie-player";
import { useComputedStyleObserver } from "../../hooks/use-computed-style-observer";
import styles from "./LoadingAnimation.module.scss";
import { LoadingAnimationPlaceholder } from "./LoadingAnimationPlaceholder";
import LoadingAnimationText from "./LoadingAnimationText";

export type LoadingAnimationProps = HTMLAttributes<HTMLDivElement> & {
    /**
     * Determines the width of the visual asset. Preserves the aspect ratio of the asset. Accepts a valid CSS sizing unit
     * @default 100px
     */
    width?: string;
    /**
     * Loading text to display
     */
    text?: string[] | string;
    /**
     * Option to animate the text when there a multiple strings in the text array. The text will fade in and out sequentially
     * @default false
     */
    animateText?: boolean;
    /**
     * @default Loading
     * Screen reader text
     */
    accessibleText?: string;
};

export const LoadingAnimation: FC<LoadingAnimationProps> = ({
    width = "100px",
    text,
    accessibleText = "Loading",
    animateText = false,
    ...divProps
}) => {
    const lottieAnimationSrc =
        "--c-loading-animation-plane-window-asset-source";
    const loadingAnimationLottieUrl = decodeURIComponent(
        useComputedStyleObserver(lottieAnimationSrc),
    );

    return (
        <div {...divProps} className={styles["loading-animation"]}>
            <div
                className={styles["loading-animation-lottie-wrapper"]}
                style={{ ["--width"]: width || undefined } as CSSProperties}
            >
                <div
                    aria-hidden
                    className={styles["loading-animation-placeholder"]}
                >
                    <LoadingAnimationPlaceholder />
                </div>
                <Lottie
                    loop
                    play
                    path={loadingAnimationLottieUrl}
                    className={styles["loading-animation-lottie"]}
                    role="img"
                    aria-label={accessibleText || undefined}
                    aria-hidden={!accessibleText || undefined}
                    rendererSettings={{ title: "Loading" }}
                />
            </div>
            {text && (
                <p className={styles["loading-animation-text"]}>
                    <LoadingAnimationText
                        loadingText={text}
                        animateText={animateText}
                    />
                </p>
            )}
        </div>
    );
};
